import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import config from '../config';
import { billingApi } from '../services/api';
import SignupForm from './SignupForm';

const CHECKOUT_APPEARANCE = {
  theme: 'stripe',
  labels: 'auto',
  inputs: 'spaced',
  variables: {
    borderRadius: '4px',
    colorBackground: '#ffffff',
    colorDanger: '#df1b41',
    colorPrimary: '#0570de',
    colorSuccess: '#00c853',
    colorText: '#30313d',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSizeBase: '16px',
    spacingUnit: '4px'
  }
};

const StripeCheckoutModal = ({ plan, signedIn, onClose, onComplete }) => {
  const dialog = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const paid = plan.id !== 'tier_0';
  const [loading, setLoading] = useState(signedIn && paid);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const appRoot = document.getElementById('root');
    const previousInert = appRoot?.inert;
    // Only the app is inert; Stripe's body-level test tools must stay interactive.
    if (appRoot) appRoot.inert = true;
    element.querySelector('button').focus();
    document.body.style.overflow = 'hidden';
    return () => {
      if (appRoot) appRoot.inert = previousInert;
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    if (!signedIn || !paid) return undefined;
    let active = true;
    let form;
    setLoading(true);
    setError('');
    const initialize = async () => {
      try {
        if (!config.stripePublishableKey) throw new Error('Stripe checkout is not configured.');
        if (typeof window.Stripe !== 'function') throw new Error('Stripe.js did not load. Refresh the page and try again.');
        const session = plan.action === 'Upgrade'
          ? await billingApi.changePlan(plan.id)
          : await billingApi.createCheckoutSession(plan.id, 'monthly');
        if (!active) return;
        if (!session.client_secret) throw new Error('The server did not return a Checkout client secret.');
        const stripe = window.Stripe(config.stripePublishableKey);
        const dark = document.documentElement.dataset.theme !== 'light';
        const checkout = stripe.initCheckoutFormSdk({
          clientSecret: Promise.resolve(session.client_secret),
          appearance: dark ? {
            ...CHECKOUT_APPEARANCE,
            theme: 'night',
            variables: {
              ...CHECKOUT_APPEARANCE.variables,
              colorBackground: '#141414',
              colorText: '#f3f1ec',
              colorPrimary: '#ffc42c',
              colorDanger: '#f08a80',
              colorSuccess: '#7fcf98'
            }
          } : CHECKOUT_APPEARANCE
        });
        form = checkout.createForm({ layout: 'expanded' });
        form.mount('#checkout-form');
        const result = await checkout.loadActions();
        if (!active) return;
        if (result.type !== 'success') throw new Error('Stripe could not initialize the payment form.');
        form.on('confirm', async (event) => {
          if (!active) return;
          setConfirming(true);
          setError('');
          try {
            const confirmation = await result.actions.confirm({
              formConfirmEvent: event,
              returnUrl: new URL('/plans?checkout=complete', window.location.origin).href
            });
            if (active && confirmation.type === 'error') {
              setError(confirmation.error.message || 'Payment confirmation failed.');
            }
            if (active && confirmation.type === 'success') onComplete?.();
          } catch (err) {
            if (active) setError(err.message || 'Payment confirmation failed.');
          } finally {
            if (active) setConfirming(false);
          }
        });
      } catch (err) {
        if (active) setError(err.message || 'Unable to start Stripe checkout.');
      } finally {
        if (active) setLoading(false);
      }
    };
    initialize();
    return () => {
      active = false;
      form?.destroy();
    };
  }, [plan.id, plan.action, attempt, signedIn, paid, onComplete]);

  return createPortal(
    <div className="modal-backdrop" role="presentation">
    <section
      ref={dialog}
      className="stripe-checkout-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-title"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (!confirming) onClose();
        }
        if (event.key === 'Tab') {
          const controls = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], iframe, [tabindex="0"]')];
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
          if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
        }
      }}
    >
      <div className="checkout-modal-header">
        <h2 id="checkout-title">{paid ? `${plan.action || 'Subscribe'} to ${plan.label}` : signedIn ? 'Your free account is ready' : 'Create your free account'}</h2>
        <button className="ghost-button" type="button" aria-label="Close checkout" disabled={confirming} onClick={onClose}>Close</button>
      </div>
      {plan.action === 'Upgrade' && <p>You will pay the full monthly price difference now, plus applicable tax, regardless of the days remaining. Your new plan activates after payment is verified and renews at its full monthly price on the next 1st (America/New_York).</p>}
      {signedIn && paid ? (
        <>
          {loading && <p role="status">Preparing secure checkout...</p>}
          {error && <div className="error-box" role="alert">{error}</div>}
          {error && !confirming && <button className="primary-button" type="button" onClick={() => setAttempt((value) => value + 1)}>Retry checkout</button>}
          <div id="checkout-form" aria-busy={loading} />
        </>
      ) : signedIn ? <div><p>Your account has Free Tier access. No payment card is required.</p><a className="primary-button" href="/">Open your workspace</a></div> : <SignupForm paid={paid} />}
    </section>
    </div>,
    document.body
  );
};

export default StripeCheckoutModal;
